import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  WAREHOUSE_ENDPOINTS,
  IS_WAREHOUSE_SELECTION_MODAL_ENABLED,
  appendWarehouseQueryToInternalUrl,
  getCurrentWarehouse,
  getDefaultWarehouse,
  getWarehouseById,
  getWarehouseIdFromSearchParams,
  hasStoredWarehouseSelection,
  isAppRouteUrl,
  persistWarehouseSelection,
  stripWarehouseSelectionQueryParams,
  type WarehouseEndpoint,
} from '@/features/warehouse/config'
import { getStoredTokenForWarehouse } from '@/api/client'
import {
  clearPendingWarehouseAuthSwitchNotice,
  setPendingWarehouseAuthSwitchNotice,
} from '@/features/warehouse/authSwitchNotice'
import { markPendingWarehouseSwitch } from '@/features/warehouse/switchTransition'

type GeolocationStatus =
  | 'idle'
  | 'requesting'
  | 'granted'
  | 'denied'
  | 'unavailable'
  | 'unsupported'

type WarehouseContextValue = {
  warehouses: WarehouseEndpoint[]
  selectedWarehouse: WarehouseEndpoint
  selectedWarehouseId: string
  suggestedWarehouse: WarehouseEndpoint | null
  geolocationStatus: GeolocationStatus
  shouldPromptSelection: boolean
  setSelectedWarehouse: (warehouseId: string, options?: { reload?: boolean; force?: boolean }) => void
  completeInitialSelection: (warehouseId: string) => void
}

const WarehouseContext = createContext<WarehouseContextValue | null>(null)

const toRadians = (value: number): number => (value * Math.PI) / 180

const calculateDistanceKm = (
  latitudeA: number,
  longitudeA: number,
  latitudeB: number,
  longitudeB: number
): number => {
  const earthRadiusKm = 6371
  const dLat = toRadians(latitudeB - latitudeA)
  const dLon = toRadians(longitudeB - longitudeA)
  const lat1 = toRadians(latitudeA)
  const lat2 = toRadians(latitudeB)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const findNearestWarehouse = (
  latitude: number,
  longitude: number
): WarehouseEndpoint | null => {
  const warehousesWithCoords = WAREHOUSE_ENDPOINTS.filter(
    (warehouse) => warehouse.latitude != null && warehouse.longitude != null
  )

  if (warehousesWithCoords.length === 0) {
    return null
  }

  return warehousesWithCoords.reduce<WarehouseEndpoint | null>((nearest, warehouse) => {
    if (warehouse.latitude == null || warehouse.longitude == null) {
      return nearest
    }

    if (nearest == null || nearest.latitude == null || nearest.longitude == null) {
      return warehouse
    }

    const nearestDistance = calculateDistanceKm(
      latitude,
      longitude,
      nearest.latitude,
      nearest.longitude
    )

    const currentDistance = calculateDistanceKm(
      latitude,
      longitude,
      warehouse.latitude,
      warehouse.longitude
    )

    return currentDistance < nearestDistance ? warehouse : nearest
  }, null)
}

const getWarehouseSwitchRedirectUrl = (warehouseId: string): string => {
  if (typeof window === 'undefined') {
    return '/'
  }

  const { pathname, search, hash } = window.location

  if (pathname.startsWith('/products/')) {
    return appendGeoQueryToUrl('/catalog', '', '', warehouseId)
  }

  if (pathname.startsWith('/catalog')) {
    const params = new URLSearchParams(stripWarehouseSelectionQueryParams(search))
    params.delete('category')
    params.delete('page')
    return appendGeoQueryToUrl(
      pathname,
      params.toString() ? `?${params.toString()}` : '',
      hash,
      warehouseId
    )
  }

  return appendGeoQueryToUrl(
    pathname,
    stripWarehouseSelectionQueryParams(search),
    hash,
    warehouseId
  )
}

const appendGeoQueryToUrl = (
  pathname: string,
  search: string,
  hash: string,
  warehouseId: string
): string => {
  const params = new URLSearchParams(stripWarehouseSelectionQueryParams(search))
  params.set('geo', warehouseId)
  const nextSearch = params.toString()

  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`
}

export const WarehouseProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>(() => getCurrentWarehouse().id)
  const [suggestedWarehouseId, setSuggestedWarehouseId] = useState<string | null>(null)
  const [geolocationStatus, setGeolocationStatus] = useState<GeolocationStatus>('idle')
  const [shouldPromptSelection, setShouldPromptSelection] = useState<boolean>(
    () => IS_WAREHOUSE_SELECTION_MODAL_ENABLED && !hasStoredWarehouseSelection()
  )

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const scopeAnchor = (anchor: HTMLAnchorElement) => {
      const rawHref = anchor.getAttribute('href')
      if (!rawHref) {
        return
      }

      const scopedHref = appendWarehouseQueryToInternalUrl(rawHref, selectedWarehouseId)
      if (scopedHref !== rawHref) {
        anchor.setAttribute('href', scopedHref)
      }
    }

    const scopeAnchors = () => {
      document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(scopeAnchor)
    }

    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return
      }

      const target = event.target
      if (!(target instanceof Element)) {
        return
      }

      const anchor = target.closest<HTMLAnchorElement>('a[href]')
      if (!anchor || anchor.target || anchor.hasAttribute('download')) {
        return
      }

      const rawHref = anchor.getAttribute('href')
      if (!rawHref) {
        return
      }

      if (!isAppRouteUrl(rawHref)) {
        return
      }

      const scopedHref = appendWarehouseQueryToInternalUrl(rawHref, selectedWarehouseId)
      event.preventDefault()
      event.stopPropagation()
      anchor.setAttribute('href', scopedHref)
      navigate(scopedHref)
    }

    scopeAnchors()

    const observer = new MutationObserver(scopeAnchors)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    })
    document.addEventListener('click', handleClick, true)

    return () => {
      observer.disconnect()
      document.removeEventListener('click', handleClick, true)
    }
  }, [navigate, selectedWarehouseId])

  useEffect(() => {
    if (!IS_WAREHOUSE_SELECTION_MODAL_ENABLED) {
      return
    }

    if (!shouldPromptSelection) {
      return
    }

    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      setGeolocationStatus('unsupported')
      return
    }

    setGeolocationStatus('requesting')

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nearestWarehouse = findNearestWarehouse(
          position.coords.latitude,
          position.coords.longitude
        )

        setSuggestedWarehouseId(nearestWarehouse?.id ?? getDefaultWarehouse().id)
        setGeolocationStatus('granted')
      },
      (error) => {
        setSuggestedWarehouseId(getDefaultWarehouse().id)

        if (error.code === error.PERMISSION_DENIED) {
          setGeolocationStatus('denied')
          return
        }

        setGeolocationStatus('unavailable')
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 1000 * 60 * 60,
      }
    )
  }, [shouldPromptSelection])

  useEffect(() => {
    const warehouseIdFromSearch = getWarehouseIdFromSearchParams(
      new URLSearchParams(location.search)
    )
    const warehouseFromSearch = getWarehouseById(warehouseIdFromSearch)

    if (!warehouseFromSearch || warehouseFromSearch.id === selectedWarehouseId) {
      return
    }

    persistWarehouseSelection(warehouseFromSearch.id)
    setSelectedWarehouseId(warehouseFromSearch.id)
    setShouldPromptSelection(false)
  }, [location.search, selectedWarehouseId])

  useEffect(() => {
    if (shouldPromptSelection) {
      return
    }

    const params = new URLSearchParams(location.search)
    const currentGeo = params.get('geo')
    const hasLegacyWarehouseParam = params.has('warehouse')

    if (currentGeo === selectedWarehouseId && !hasLegacyWarehouseParam) {
      return
    }

    if (currentGeo && currentGeo !== selectedWarehouseId) {
      return
    }

    const nextUrl = appendGeoQueryToUrl(
      location.pathname,
      location.search,
      location.hash,
      selectedWarehouseId
    )
    navigate(nextUrl, { replace: true })
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    selectedWarehouseId,
    shouldPromptSelection,
  ])

  const setSelectedWarehouse = useCallback(
    (warehouseId: string, options?: { reload?: boolean; force?: boolean }) => {
      const warehouse = getWarehouseById(warehouseId)

      if (!warehouse) {
        return
      }

      if (warehouse.id === selectedWarehouseId && options?.reload === false && !options?.force) {
        return
      }

      const shouldReload = options?.reload !== false

      if (typeof window !== 'undefined') {
        const nextUrl = appendGeoQueryToUrl(
          window.location.pathname,
          window.location.search,
          window.location.hash,
          warehouse.id
        )

        if (shouldReload) {
          window.history.replaceState(window.history.state, '', nextUrl)
        } else {
          navigate(nextUrl, { replace: true })
        }
      }

      if (getStoredTokenForWarehouse(warehouse.id)) {
        setPendingWarehouseAuthSwitchNotice({
          warehouseId: warehouse.id,
          warehouseName: warehouse.name,
          createdAt: Date.now(),
        })
      } else {
        clearPendingWarehouseAuthSwitchNotice()
      }

      persistWarehouseSelection(warehouse.id)
      setSelectedWarehouseId(warehouse.id)
      setShouldPromptSelection(false)

      if (typeof window !== 'undefined' && shouldReload) {
        markPendingWarehouseSwitch(warehouse.id)
        window.location.assign(getWarehouseSwitchRedirectUrl(warehouse.id))
      }
    },
    [navigate, selectedWarehouseId]
  )

  const completeInitialSelection = useCallback(
    (warehouseId: string) => {
      setSelectedWarehouse(warehouseId, {
        reload: warehouseId !== selectedWarehouseId,
        force: true,
      })
    },
    [selectedWarehouseId, setSelectedWarehouse]
  )

  const selectedWarehouse = useMemo(
    () => getWarehouseById(selectedWarehouseId) ?? getDefaultWarehouse(),
    [selectedWarehouseId]
  )

  const suggestedWarehouse = useMemo(
    () => getWarehouseById(suggestedWarehouseId) ?? null,
    [suggestedWarehouseId]
  )

  const value = useMemo<WarehouseContextValue>(
    () => ({
      warehouses: WAREHOUSE_ENDPOINTS,
      selectedWarehouse,
      selectedWarehouseId: selectedWarehouse.id,
      suggestedWarehouse,
      geolocationStatus,
      shouldPromptSelection,
      setSelectedWarehouse,
      completeInitialSelection,
    }),
    [
      selectedWarehouse,
      suggestedWarehouse,
      geolocationStatus,
      shouldPromptSelection,
      setSelectedWarehouse,
      completeInitialSelection,
    ]
  )

  return <WarehouseContext.Provider value={value}>{children}</WarehouseContext.Provider>
}

export const useWarehouse = (): WarehouseContextValue => {
  const context = useContext(WarehouseContext)

  if (!context) {
    throw new Error('useWarehouse must be used within WarehouseProvider')
  }

  return context
}
